import React, { useEffect, useState, useMemo, useRef } from "react"
import { useSearchspringResults } from "@app/scripts/hooks/use-searchspring-results"
import { CardProduct } from "@app/scripts/components/card-product"
import { SearchspringSearchResult, useShopifyPriceData } from "@app/types"
import { session } from "@app/scripts/lib/session"
import { useSwatchCache } from "../hooks/use-swatches"
import { currentCountryCurrency } from "@app/scripts/lib/utils"
import {
  useShopifyPrice,
  mergeResultsWithPricing,
} from "@app/scripts/hooks/use-shopify"
import { useSearchspringSearchContext } from "@app/scripts/providers/searchspring"
import {
  CollectionRecommendationCarousel,
  type RecommendationCarouselConfig,
} from "@app/scripts/components/collection-recommendation-carousel"

type Props = {
  recommendationCarousel?: RecommendationCarouselConfig | null
}

export function ResultGrid({ recommendationCarousel = null }: Props) {
  const [modelToggle, setModelToggle] = useState(true)

  // Pricing cache to avoid re-fetching prices for products already loaded
  const [pricingCache, setPricingCache] = useState<useShopifyPriceData[]>([])

  const results = useSearchspringResults()
  const swatchColourData = useSwatchCache(results)

  // Formats data for useShopifyPrice (Removes string results (call outs))
  const formattedProductData = useMemo(
    () =>
      results
        .map((result) => ({
          price: result.price || 0,
          compareAtPrice: result.msrp || 0,
          id: result.ss_id || "",
          handle: result.handle || "",
        }))
        .filter((product) => product.handle !== "" && product.id !== ""),
    [results]
  )

  // Filter products that we don't already have pricing data for
  const productsPricingToFetch = useMemo(() => {
    const cachedHandles = new Set(pricingCache.map((item) => item.handle))
    return formattedProductData.filter(
      (product) => !cachedHandles.has(product.handle)
    )
  }, [formattedProductData, pricingCache])

  // Only fetch prices for products not in cache
  const newPriceData = useShopifyPrice(
    productsPricingToFetch,
    currentCountryCurrency
  )

  // Update pricing cache when new price data arrives
  useEffect(() => {
    if (newPriceData.length > 0) {
      setPricingCache((prevCache) => {
        const existingMap = new Map(
          prevCache.map((item) => [item.handle, item])
        )

        newPriceData.forEach((item) => {
          existingMap.set(item.handle, item)
        })

        return Array.from(existingMap.values())
      })
    }
  }, [newPriceData])

  // Create new products array with updated price data or defaults back to results
  const productsWithUpdatedPrices = useMemo(() => {
    return mergeResultsWithPricing(results, pricingCache)
  }, [results, pricingCache])

  const { response, isLoading } = useSearchspringSearchContext()

  // The provider's error state is not set on the very first request (its
  // mounted check reads false during the initial render), so track "a request
  // ran and finished with no response" here to catch a failed first load
  const requestStartedRef = useRef(false)
  const [requestFailed, setRequestFailed] = useState(false)

  useEffect(() => {
    if (isLoading) requestStartedRef.current = true
    else if (requestStartedRef.current && response === undefined)
      setRequestFailed(true)
  }, [isLoading, response])

  // Skeleton product cards stay visible while the first request is loading,
  // then hide once there are results, a response arrives with no products
  // for the requested page, or the request failed, so the skeleton never
  // lingers over an empty grid. "No products for the page" covers empty
  // collections and searches, and a page past the end (for example ?page=2
  // kept after a filter leaves one page of results, where totalResults is
  // still above zero). A response that has products waits for them to
  // render, which avoids a blank frame between the two
  useEffect(() => {
    const skeletonProductCardContainer = document.querySelector(
      ".main-collection-product-grid__skeleton"
    )

    if (
      productsWithUpdatedPrices.length > 0 ||
      (response !== undefined && (response.results?.length ?? 0) === 0) ||
      requestFailed
    ) {
      skeletonProductCardContainer?.classList.remove("is-visible-grid")
      skeletonProductCardContainer?.classList.add("is-hidden")
    }
  }, [productsWithUpdatedPrices, response, requestFailed])

  const handleClick = (event: MouseEvent) => {
    if (event?.target?.type === "checkbox") {
      setModelToggle(event?.target?.checked)
    }
  }

  useEffect(() => {
    const handleDelegatedClick = (event: MouseEvent) => {
      const toggle = event?.target?.closest(".collection-banner--model-toggle")
      if (toggle) {
        handleClick(event)
      }
    }

    document.addEventListener("click", handleDelegatedClick)

    return () => {
      document.removeEventListener("click", handleDelegatedClick)
    }
  }, [])

  useEffect(() => {
    const savedView = session.imageTypeView.value || true
    setModelToggle(savedView)
  }, [])

  useEffect(() => {
    session.imageTypeView.value = modelToggle
  }, [modelToggle])

  if (!productsWithUpdatedPrices.length) return null

  // Insert the recommendation carousel once, straight after the grid cell at
  // the configured position. Inline banners occupy a cell, so they count too;
  // otherwise a banner before the insertion point would leave empty cells
  // ahead of the full-width row. Load More appends pages after earlier ones,
  // so the carousel keeps its place.
  const gridItems: React.ReactNode[] = []
  let cellCount = 0

  const pushCarouselIfDue = () => {
    cellCount += 1
    if (
      recommendationCarousel &&
      cellCount === recommendationCarousel.position
    ) {
      gridItems.push(
        <CollectionRecommendationCarousel
          key="recommendation-carousel"
          config={recommendationCarousel}
        />
      )
    }
  }

  productsWithUpdatedPrices.forEach(
    (result: string | SearchspringSearchResult) => {
      if (typeof result === "string") {
        gridItems.push(
          <div
            key={result}
            className="ss-inline-banner"
            dangerouslySetInnerHTML={{ __html: result }}
          />
        )
        pushCarouselIfDue()
        return
      }

      gridItems.push(
        <div key={result.handle}>
          <CardProduct
            source="searchspring"
            data={result}
            modelToggle={modelToggle}
            swatchColours={swatchColourData}
            currentCountryCurrency={currentCountryCurrency}
            useInteractiveSwatches={true}
          />
        </div>
      )

      pushCarouselIfDue()
    }
  )

  return <div className="ss-product-grid">{gridItems}</div>
}
